import { h } from 'preact'
import { useState } from 'preact/hooks'
import { initI18n, i18n } from 'i18n/i18n'

import { CurrencyValueForm } from 'models/apps'
import { SpinnerError } from 'models/calculator'
import { Calculation, ExchangeDirection } from 'models/exchange-transfer-calculate'

import {
  DEFAULT_EXCHANGE_DATA,
  DEFAULT_RESPONSE,
  DISCOUNT_RATE,
  DEFAULT_MINIMAL_AMOUNT,
  PLN_MINIMAL_AMOUNT,
  SETTINGS_KEYS,
  DEFAULT_REFRESH_TIME,
  FIXED_OPTION_LIST,
  DEFAULT_DEBOUNCE_TIME,
  WITHOUT_TRANSFER_FEE,
  DEFAULT_CURRENCY,
} from './config'

import { formatCurrencyAmount } from 'apps-utils/validate-currency-input'
import { validateDiscountRate } from 'apps-utils/validate-discount'
import { getCurrencySelectOptions } from 'apps-utils/get-currency-select-options'
import { getIsBelowMinimalAmount, getMinimalAmountForCurrency } from 'apps-utils/get-is-below-minimal-amount'

import { useExchangeData } from 'hooks/useExchangeData'
import { useDefaultBaseAmount } from 'hooks/useDefaultBaseAmount'
import { useSettings } from 'hooks/useSettings'
import { useRegistryUrl } from 'hooks/useRegistryUrl'

import { ErrorSpinner } from 'common-modules/feedback/error-spinner'
import { CalculatorActualRate } from 'common-modules/calculator-actual-rate'
import { CalculatorDirectionSelect } from 'common-modules/selects/calculator-direction-select'
import { CalculatorCurrencyInput } from 'common-modules/calculator-currency-input'
import { CalculatorCurrencySelect } from 'common-modules/selects/calculator-currency-select'
import { CalculatorErrorInfo } from 'common-modules/feedback/calculator-error-info'
import { CalculatorFooter } from 'common-modules/calculator-footer'

import CalculatorHeader from './components/header'

initI18n()

const FrontpageCalculator = () => {
  const [direction, setDirection] = useState<ExchangeDirection>(DEFAULT_EXCHANGE_DATA.direction)
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_EXCHANGE_DATA.baseCurrency)
  const [counterCurrency, setCounterCurrency] = useState(DEFAULT_EXCHANGE_DATA.counterCurrency)
  const [value, setValue] = useState<CurrencyValueForm>(formatCurrencyAmount(DEFAULT_EXCHANGE_DATA.baseAmount))
  const [exchangeData, setExchangeData] = useState<Calculation>(DEFAULT_RESPONSE)
  const [spinnerError, setSpinnerError] = useState<SpinnerError>({
    isErrorVisible: false,
  })
  const [discountRate] = useState<number>(validateDiscountRate(DISCOUNT_RATE))
  const registryUrl = useRegistryUrl()

  const {
    isSettingsLoading,
    isCurrentCurrencySupported,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    supportedCurrenciesPairs,
    supportedCurrenciesPairsAllCombinations,
    currency,
    minimalAmounts,
    exchangeRatePrecision,
  } = useSettings({
    setSpinnerError,
    config: {
      SETTINGS_KEYS,
      DEFAULT_MINIMAL_AMOUNT,
      PLN_MINIMAL_AMOUNT,
      DEFAULT_CURRENCY,
    },
  })

  const isBelowMinimalAmount = getIsBelowMinimalAmount({
    value,
    minimalAmounts,
    baseCurrency,
    counterCurrency,
  })

  useDefaultBaseAmount({
    baseCurrency,
    counterCurrency,
    exchangeRatePrecision,
    minimalAmounts,
    defaultBaseAmount: DEFAULT_EXCHANGE_DATA.baseAmount,
    value,
    setValue,
  })

  const { baseCurrencyOptions, counterCurrencyOptions } = getCurrencySelectOptions({
    baseCurrency,
    counterCurrency,
    supportedCurrenciesPairsAllCombinations,
    isSettingsLoading,
  })

  useExchangeData({
    isSettingsLoading,
    direction,
    baseCurrency,
    counterCurrency,
    value,
    discountRate,
    supportedCurrenciesPairs,
    setExchangeData,
    setSpinnerError,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    isBelowMinimalAmount,
    config: {
      DEFAULT_RESPONSE,
      DEFAULT_REFRESH_TIME,
      DEFAULT_EXCHANGE_DATA,
      WITHOUT_TRANSFER_FEE,
    },
  })

  return (
    <div className="exchange-calculator">
      <CalculatorHeader />
      <div className="exchange-calculator__main">
        <CalculatorActualRate
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          exchangeData={exchangeData}
          currency={currency}
        />
        <div className="exchange-calculator__main-content">
          <div className="exchange-calculator__form">
            <CalculatorDirectionSelect
              direction={direction}
              setDirection={setDirection}
              options={FIXED_OPTION_LIST}
            />
            <CalculatorCurrencyInput
              value={value}
              setValue={setValue}
              isBelowMinimalAmount={isBelowMinimalAmount}
              debounceTime={DEFAULT_DEBOUNCE_TIME}
            />
            <div className="exchange-calculator__currency-selects">
              <CalculatorCurrencySelect
                isBaseCurrencyComponent={true}
                baseCurrency={baseCurrency}
                counterCurrency={counterCurrency}
                handleSetBaseCurrency={setBaseCurrency}
                handleSetCounterCurrency={setCounterCurrency}
                fixedCurrencyList={baseCurrencyOptions}
              />
              <p className="exchange-calculator__for">{i18n('calculator.forBuy')}</p>
              <CalculatorCurrencySelect
                isBaseCurrencyComponent={false}
                baseCurrency={baseCurrency}
                counterCurrency={counterCurrency}
                handleSetBaseCurrency={setBaseCurrency}
                handleSetCounterCurrency={setCounterCurrency}
                fixedCurrencyList={counterCurrencyOptions}
              />
            </div>
          </div>

          <CalculatorErrorInfo
            isBelowMinimalAmount={isBelowMinimalAmount}
            isCurrentCurrencySupported={isCurrentCurrencySupported}
            minimalAmount={getMinimalAmountForCurrency(minimalAmounts, baseCurrency)}
            minimalAmountCurrency={baseCurrency}
          />
        </div>
        <CalculatorFooter
          exchangeData={exchangeData}
          direction={direction}
          counterCurrency={counterCurrency}
          discountRate={discountRate}
          ctaHref={registryUrl}
        />
      </div>
      {spinnerError.isErrorVisible && <ErrorSpinner errorText={spinnerError.message} />}
    </div>
  )
}

export default FrontpageCalculator
